import { useState, useEffect, useCallback } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';

/**
 * The signed-in clinic user's own account, as held in Keycloak.
 *
 * The clinic BFF has no doctor profile (`me`/`updateDoctorProfile` are the doctor
 * BFF's — see src/graphql/typeDefs/profile.js in parsina.bff.clinic). This hook
 * only covers what genuinely exists server-side: the profile picture and the UI
 * language.
 *
 * `language` is the RECORD for which language the panel runs in — Keycloak's own
 * `locale` attribute on the user, so the sign-in page follows it too. The
 * localStorage copy I18nProvider keeps is only a cache; see src/i18n/config.js.
 */

const PROFILE_FIELDS = 'id name email avatar language';

const MY_PROFILE_QUERY = `
  query {
    myProfile { ${PROFILE_FIELDS} }
  }
`;

// `image` is a JPEG/PNG data URI, or null to remove the current picture.
const UPDATE_MY_AVATAR = `
  mutation($image: String) {
    updateMyAvatar(image: $image) { ${PROFILE_FIELDS} }
  }
`;

// A BCP-47 tag the client ships a catalogue for. The BFF rejects anything else
// rather than storing it, because this writes the attribute the sign-in page
// reads as well.
const UPDATE_MY_PREFERENCES = `
  mutation($language: String) {
    updateMyPreferences(language: $language) { ${PROFILE_FIELDS} }
  }
`;

export function useMyProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    const res = await bffQuery(MY_PROFILE_QUERY);
    setProfile(res?.myProfile ?? null);
    setError(null);
  }, []);

  useEffect(() => {
    let active = true;
    bffQuery(MY_PROFILE_QUERY)
      .then(res => { if (active) { setProfile(res?.myProfile ?? null); setError(null); } })
      .catch(err => { if (active) setError(err); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Upload (data URI) or remove (null) the picture. The server response already
  // carries the authoritative URL, so patch state rather than refetching.
  const updateAvatar = useCallback((image) => {
    setSaving(true);
    return bffMutation(UPDATE_MY_AVATAR, { image: image ?? null })
      .then(res => {
        const next = res?.updateMyAvatar ?? null;
        if (next) setProfile(next);
        return next?.avatar ?? null;
      })
      .finally(() => setSaving(false));
  }, []);

  /**
   * Persist the UI language. The caller has already repainted — I18nProvider's
   * `setLocale` is local and instant — so this only makes the choice durable,
   * and it deliberately does NOT swallow its error: the picker rolls back to
   * the previous language when this rejects, because a language that will be
   * gone at the next sign-in must not look like it was saved.
   */
  const updateLanguage = useCallback((language) => {
    setSaving(true);
    return bffMutation(UPDATE_MY_PREFERENCES, { language: language ?? null })
      .then(res => {
        const next = res?.updateMyPreferences ?? null;
        if (next) setProfile(next);
        return next?.language ?? null;
      })
      .finally(() => setSaving(false));
  }, []);

  return { profile, loading, saving, error, updateAvatar, updateLanguage, refetch: fetchProfile };
}
